import { annotatorClient } from './annotatorClient';

export interface PipelineSetting {
  key: string;
  value: string;
  description: string | null;
}

export interface PipelineStatus {
  last_scan_at: string | null;
  last_scan_imported: number;
  last_scan_errors: number;
}

export interface InferResult {
  imported: number;
  errors: number;
  files_checked: number;
  error?: string;
}

export interface UploadedInferResult {
  files_received: number;
  files_checked: number;
  imported: number;
  errors: number;
  skipped_no_detection: number;
  skipped_unsupported: number;
  frames_extracted: number;
  error?: string;
}

export interface SingleImageInferResult {
  applied: boolean;
  image: {
    id: number;
    project_id: number;
    filename: string;
    original_filename: string;
    width: number;
    height: number;
    uploaded_at: string;
    annotation_count: number;
    is_annotated: boolean;
    is_verified: boolean;
    source: string;
    thumbnail_url: string;
    url: string;
  };
  annotations: Array<{
    id: number;
    image_id: number;
    label_id: number;
    x_center: number;
    y_center: number;
    box_width: number;
    box_height: number;
    created_at: string;
    updated_at: string;
  }>;
}

export async function getPipelineSettings(): Promise<PipelineSetting[]> {
  return annotatorClient.get<PipelineSetting[]>('/settings/pipeline');
}

export async function updatePipelineSetting(key: string, value: string): Promise<PipelineSetting> {
  return annotatorClient.put<PipelineSetting>(`/settings/pipeline/${encodeURIComponent(key)}`, { value });
}

export async function getPipelineStatus(): Promise<PipelineStatus> {
  return annotatorClient.get<PipelineStatus>('/pipeline/status');
}

export async function triggerScanNow(): Promise<{ status: string; imported?: number; errors?: number }> {
  return annotatorClient.post('/pipeline/scan-now');
}

export async function runProjectInference(projectId: number, lookbackMinutes: number): Promise<InferResult> {
  return annotatorClient.post<InferResult>(`/pipeline/projects/${projectId}/infer`, {
    lookback_minutes: lookbackMinutes,
  });
}

export async function inferProjectImage(
  projectId: number,
  imageId: number,
  confidence?: number,
): Promise<SingleImageInferResult> {
  return annotatorClient.post<SingleImageInferResult>(`/pipeline/projects/${projectId}/images/${imageId}/infer`, {
    confidence,
  });
}

export async function runUploadedProjectInference(
  projectId: number,
  files: File[],
  options: { frameIntervalSeconds: number; maxFrames: number },
): Promise<UploadedInferResult> {
  const form = new FormData();
  files.forEach((file) => {
    const relativeName = (file as File & { webkitRelativePath?: string }).webkitRelativePath || file.name;
    form.append('files', file, relativeName);
  });
  form.append('frame_interval_seconds', String(options.frameIntervalSeconds));
  form.append('max_frames', String(options.maxFrames));
  return annotatorClient.postForm<UploadedInferResult>(`/pipeline/projects/${projectId}/infer-upload`, form);
}
